import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  HStack,
  RadioButton,
  Typography,
  VStack,
} from '@vivaahealth/design-system';

import * as api from '../../../api';
import { getFormattedCurrencyAmount } from '../PaymentsPage/utils';
import { IsCheckedObj, IsCheckedObjKey, PartialBalanceInfo } from '.';
import CurrencyInput from './CurrencyInput';
import { isPartialBalanceValid } from './utils';

import styles from './index.module.css';

interface Props {
  isCheckedObj: IsCheckedObj;
  onClickCheck: ({
    key,
    force,
  }: {
    key: IsCheckedObjKey;
    force?: boolean;
  }) => void;
  previousBalance: number;
  currency: api.Payment.Currency;
  partialBalanceInfo: PartialBalanceInfo;
  setPartialBalanceInfo: (
    value: React.SetStateAction<PartialBalanceInfo>,
  ) => void;
  showBalanceOnly: boolean;
}

export const PriorBalance: React.FC<Props> = ({
  isCheckedObj,
  onClickCheck,
  previousBalance,
  currency,
  partialBalanceInfo,
  setPartialBalanceInfo,
  showBalanceOnly,
}) => {
  const { t } = useTranslation();

  const { balanceCheck, partialBalanceCheck } = isCheckedObj;
  const {
    amount: partialBalanceAmount,
    error,
    isEnabled: isPartialBalanceEnabled,
  } = partialBalanceInfo;

  const resetPartialBalanceAmountAndError = useCallback(() => {
    setPartialBalanceInfo((prevState) => ({
      ...prevState,
      amount: null,
      error: '',
    }));
  }, [setPartialBalanceInfo]);

  const handleClickBalanceCheck = useCallback(() => {
    if (balanceCheck) {
      resetPartialBalanceAmountAndError();
      onClickCheck({ key: 'partialBalanceCheck', force: false });
    }
    onClickCheck({ key: 'balanceCheck' });
  }, [balanceCheck, onClickCheck, resetPartialBalanceAmountAndError]);

  const handleClickPayInFull = useCallback(() => {
    resetPartialBalanceAmountAndError();
    onClickCheck({ key: 'partialBalanceCheck', force: false });
  }, [onClickCheck, resetPartialBalanceAmountAndError]);

  const handleClickPayPartial = useCallback(() => {
    onClickCheck({ key: 'partialBalanceCheck', force: true });
  }, [onClickCheck]);

  const handleBlurPartialBalance = useCallback(() => {
    if (!isPartialBalanceValid(partialBalanceAmount, previousBalance)) {
      setPartialBalanceInfo((prevState) => ({
        ...prevState,
        error: t('Please enter a valid payment amount.'),
      }));
    }
  }, [partialBalanceAmount, previousBalance, setPartialBalanceInfo, t]);

  const handleChangePartialBalance = useCallback(
    (amount: number) => {
      setPartialBalanceInfo((prevState) => ({
        ...prevState,
        error: '',
        amount,
      }));
    },
    [setPartialBalanceInfo],
  );

  return (
    <VStack gap={12}>
      <HStack justifyContent="space-between" alignItems="center">
        {showBalanceOnly ? (
          <Typography variant="title-large" bold>
            {t('Prior balance')}
          </Typography>
        ) : (
          <Checkbox
            checked={balanceCheck}
            label={t('Prior balance')}
            onChange={handleClickBalanceCheck}
          />
        )}
        <Typography variant="title-large" bold={showBalanceOnly}>
          {getFormattedCurrencyAmount(previousBalance, currency || 'usd')}
        </Typography>
      </HStack>
      {isPartialBalanceEnabled && balanceCheck && (
        <VStack paddingLeft={showBalanceOnly ? 2 : 32} gap={12}>
          <RadioButton
            label={t('Pay in full')}
            name="balance-option"
            checked={!partialBalanceCheck}
            onChange={handleClickPayInFull}
          />
          <RadioButton
            label={t('Pay partial')}
            name="balance-option"
            checked={partialBalanceCheck}
            onChange={handleClickPayPartial}
          />
          {partialBalanceCheck && (
            <VStack gap={8}>
              <CurrencyInput
                className={styles.PartialBalanceTextField}
                error={error}
                max={previousBalance}
                onBlur={handleBlurPartialBalance}
                onValueChange={handleChangePartialBalance}
                value={partialBalanceAmount ?? 0}
              />
              {!error && partialBalanceAmount ? (
                <Typography
                  className={styles.PartialBalanceHelperText}
                  variant="paragraph-medium"
                >
                  {t('Balance after payment: {{amount}}', {
                    amount: getFormattedCurrencyAmount(
                      previousBalance - partialBalanceAmount,
                      currency || 'usd',
                    ),
                  })}
                </Typography>
              ) : null}
            </VStack>
          )}
        </VStack>
      )}
      <hr className={styles.Divider} />
    </VStack>
  );
};
